import { FC, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import ponchoImg from '../../media/poncho.png';
import jaroImg from '../../media/jaro.png';
import basiaImg from '../../media/basia.png';
import zusiaImg from '../../media/zusia.png';
import { Typography, Popper, Box, ClickAwayListener, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useTranslations from '../global/translation/hooks/translation';
import { JobModal } from '../global/JobModal';
import { useThemeContext } from '../global/ThemeContext';
import useImagePreload from '../../hooks/useImagePreload';
import { designTokens } from '../../theme/designTokens';

const Team: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const percentAmount = '8rem';
  const { darkMode } = useThemeContext();
  const palette = darkMode ? designTokens.colors.dark : designTokens.colors.light;
  const [selectedMemberIndex, setSelectedMemberIndex] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>(null);
  const preloadImages = useMemo(() => [ponchoImg, basiaImg, jaroImg, zusiaImg], []);

  // Warm the cache for team images on first mount
  useImagePreload(preloadImages);

  const handleClose = () => {
    setSelectedMemberIndex(null);
    setAnchorEl(null);
  };

  const teamMembers = [
    {
      name: currentLanguage === 'pl' ? 'Poncho' : 'Poncho',
      description: translations.label?.teamPonchoDesc?.[currentLanguage] || (currentLanguage === 'pl' ? 'Koń treningowy' : 'Training Horse'),
      img: ponchoImg,
    },
    {
      name: currentLanguage === 'pl' ? 'Barbara' : 'Barbara',
      description: translations.label?.teamBasiaDesc?.[currentLanguage] || (currentLanguage === 'pl' ? 'Główny szkoleniowiec' : 'Head Trainer'),
      img: basiaImg,
    },
    {
      name: currentLanguage === 'pl' ? 'Jarosław' : 'Jarosław',
      description: translations.label?.teamJaroDesc?.[currentLanguage] || (currentLanguage === 'pl' ? 'Asystent trenera' : 'Trainer Assistant'),
      img: jaroImg,
    },
    {
      name: 'Zuzanna',
      description: translations.label?.teamZosiaDesc?.[currentLanguage] || (currentLanguage === 'pl' ? 'Opiekun koni' : 'Horse Care Specialist'),
      img: zusiaImg,
    },
  ];
  const selectedMember = selectedMemberIndex === null ? null : teamMembers[selectedMemberIndex];
  return (
    <Grid
      my={6}
      py={{ xs: 6, md: 10 }}
      container
      justifyContent="center"
      alignItems="center"
      sx={{
        bgcolor: darkMode ? palette.background.default : 'transparent',
        maxWidth: '1200px',
        mx: 'auto',
        px: { xs: 2, md: 4 },
      }}
    >
      <Grid item xs={12} mb={4}>
        <Typography variant="h4" fontWeight={300} sx={{ color: darkMode ? '#FFFFFF' : palette.primary, letterSpacing: '-0.5px' }} textAlign="center">
          {translations.menuItems.ourTeam[currentLanguage]}
        </Typography>
      </Grid>
      <Grid container justifyContent="center" alignItems="center" onClick={handleClose}>
        {teamMembers.map((member, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Box
              sx={{
                position: 'relative',
                paddingTop: percentAmount,
                m: 2,
                cursor: 'pointer',
                transition: 'transform 0.3s ease-in-out',
                transform: 'translateY(0)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMemberIndex(index);
                setAnchorEl(e.currentTarget);
              }}
            >
              <Box
                component="img"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'scale-down',
                  transition: 'transform 0.3s ease',
                }}
                src={member.img}
                alt={member.name}
                loading={index < 2 ? 'eager' : 'lazy'}
                decoding="async"
                width={256}
                height={256}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
      <Grid item xs={12}>
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs>
            <JobModal percentAmount={percentAmount} />
          </Grid>
        </Grid>
      </Grid>

      <Popper
        open={!!selectedMember}
        anchorEl={anchorEl}
        placement="bottom"
        modifiers={[
          {
            name: 'flip',
            enabled: true,
            options: {
              altBoundary: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
          {
            name: 'arrow',
            enabled: true,
            options: {
              element: arrowRef,
            },
          },
          {
            name: 'offset',
            enabled: true,
            options: {
              offset: [0, 12],
            },
          },
        ]}
        sx={{
          zIndex: 1300,
          '&[data-popper-placement*="bottom"] .MuiPopper-arrow': {
            top: 0,
            left: 0,
            marginTop: '-0.71em',
            width: '1em',
            height: '0.71em',
            '&::before': {
              content: '""',
              margin: 'auto',
              display: 'block',
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '0 0.71em 0.71em 0.71em',
              borderColor: `transparent transparent ${darkMode ? palette.background.elevated : '#FFFFFF'} transparent`,
            },
          },
        }}
      >
        <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseDown">
          <Box
            sx={{
              backgroundColor: darkMode ? palette.background.elevated : '#FFFFFF',
              border: `1px solid ${darkMode ? palette.border.default : 'rgba(20, 56, 125,0.12)'}`,
              boxShadow: darkMode ? '0 8px 16px rgba(0, 0, 0, 0.3)' : '0 14px 30px rgba(20, 56, 125,0.12)',
              borderRadius: '16px',
              p: 3,
              position: 'relative',
              maxWidth: '400px',
            }}
          >
            <Box ref={setArrowRef} className="MuiPopper-arrow" />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 1.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: darkMode ? '#FFFFFF' : palette.primary,
                  fontWeight: 400,
                  fontSize: '18px',
                  letterSpacing: '-0.3px',
                }}
              >
                {selectedMember?.name}
              </Typography>
              <IconButton
                size="small"
                aria-label={currentLanguage === 'pl' ? 'Zamknij informacje o członku zespołu' : 'Close team member details'}
                onClick={handleClose}
                sx={{
                  flex: '0 0 auto',
                  ml: 'auto',
                  mr: -1,
                  color: darkMode ? palette.text.secondary : palette.text.primary,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: darkMode ? '#CCCCCC' : '#555555',
                lineHeight: 1.7,
                fontSize: '15px',
              }}
            >
              {selectedMember?.description}
            </Typography>
          </Box>
        </ClickAwayListener>
      </Popper>
    </Grid>
  );
};

export default Team;
